import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { TicketStatusEnum } from "@/lib/enum/ticket-status.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { ICustomViewableEntity } from "@/lib/interfaces/custom-viewable-entity.interface";
import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";
import { ICreateDto } from "@/lib/utility/data/create-dto.interface";
import { formatHoursAndMinutes, formatYearsMonthDay } from "@/lib/utility/date-helper";
import { handleError } from "@/lib/utility/handleError";
import { TicketGoToHelper } from "@/lib/utility/ticket.go-to-helper";
import ticketService from "@/services/mrfiktiv/services/ticketService";
import {
  MrfiktivCreateTicketDtoGen,
  MrfiktivTicketViewModelGen,
  MrfiktivUpdateTicketDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { ticketStateMap } from "@/store/mapper/ticket-state.map";
import { ProjectDataAccessLayer } from "@/store/modules/access-layers/project.access-layer";
import { TagDataAccessLayer } from "@/store/modules/access-layers/tag.access-layer";
import { TicketDataAccessLayer } from "@/store/modules/access-layers/ticket.access-layer";
import { FleetAggregationModule } from "@/store/modules/fleet-aggregation.store";
import { PartnerUserModule } from "@/store/modules/partner-user.store";
import Vue from "vue";
import VueRouter from "vue-router";
import { ActivityLog } from "./activity-log.entity";
import { CustomFieldValue, ICustomFieldValue } from "./custom-field-value.entity";
import { IReference, Reference } from "./reference.entity";
import { IShortUser, ShortUser } from "./short-user.entity";
import { ITag, Tag } from "./tag.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { VehicleReference } from "./vehicle-reference.entity";

@IsFilterable
class TicketBase
  implements
    Omit<MrfiktivTicketViewModelGen, "values">,
    ICreateDto<ITicket>,
    ICustomViewableEntity<ITicket, MrfiktivUpdateTicketDtoGen> {
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.ticket.id",
    config: {
      itemCallback: () => TicketDataAccessLayer.entities,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-ticket"
    }
  })
  id = "";

  /** identifier of partner that manages ticket */
  partnerId = "";

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.ticket.userId",
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  userId? = "";

  /** The user */
  user?: IShortUser;

  /** timestamp of the ticket */
  @FilterConfig({ type: Timestamp })
  timestamp: ITimestamp;

  /** unique number of the ticket */

  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.ticket.number",
    width: "100"
  })
  number = -1;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.ticket.projectId",
    config: {
      items: ProjectDataAccessLayer.entities,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-project"
    }
  })
  projectId?: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.ticket.title",
    width: "350"
  })
  title = "";

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.ticket.body"
  })
  body = "";

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.company.tagIds",
    config: {
      itemCallback: () => TagDataAccessLayer.entities,
      mapItemToComponent: item => ({ value: item, size: "small" }),
      itemValue: "id",
      component: "tag-chip"
    }
  })
  tagIds: string[] = [];

  tags: ITag[] = [];

  @FilterConfig({
    displayName: "objects.ticket.assignees",
    type: FilterTypes.OBJECT_ID,
    width: "120",
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  assignees: string[];

  assigneesDetails?: IShortUser[];

  @FilterConfig({
    displayName: "objects.ticket.state",
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(TicketStatusEnum).map(e => {
        return {
          text: `enums.TicketStatusEnum.${e}`,
          value: e
        } as IVSelectItem;
      }),
      itemValue: "value"
    },
    width: "85"
  })
  state?: TicketStatusEnum;

  /**
   * when the ticket was closed
   * @format date-time
   */
  @FilterConfig({
    displayName: "objects.ticket.closedAt",
    type: FilterTypes.DATE
  })
  closedAt?: string;

  /** user id that closed the ticket */
  @FilterConfig({
    displayName: "objects.ticket.closedBy",
    type: FilterTypes.OBJECT_ID,
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  closedBy?: string;

  /** user details that closed the ticket */
  closedByDetails?: IShortUser;

  /** values of the custom fields */
  @FilterConfig({ type: CustomFieldValue })
  values: ICustomFieldValue[] = [];

  /** The references of the ticket */

  @FilterConfig({
    type: VehicleReference
  })
  refs?: IReference[];

  dueDate?: string;
  dueTime?: string = "00:00";

  loading = false;

  @FilterConfig({
    displayName: "objects.ticket.due",
    type: FilterTypes.DATE
  })
  get due(): string | undefined {
    if (this.dueDate) {
      const d = new Date(this.dueDate);

      // If no due time set it to "00:00" local time
      if (!this.dueTime) {
        this.dueTime = "00:00";
      }
      const [hours, minutes] = this.dueTime.split(":");
      d.setUTCHours(+hours, +minutes);
      return d.toISOString();
    }

    return undefined;
  }

  set due(due: string | undefined) {
    if (!due) {
      return;
    }

    const d = new Date(due);
    this.dueDate = formatYearsMonthDay(d);
    this.dueTime = formatHoursAndMinutes(d);
  }

  get dueDateTime() {
    if (this.dueDate) {
      return new Date(this.dueDate).getTime();
    }

    return undefined;
  }

  get baseUpdateDto() {
    const dto: MrfiktivUpdateTicketDtoGen = {
      projectId: this.projectId,
      tagIds: this.tags.map(tag => tag.id) || []
    };

    if (this.due) {
      dto.due = this.due;
    } else {
      dto.due = "";
    }

    return dto;
  }

  get updateDto(): MrfiktivUpdateTicketDtoGen {
    const dto: MrfiktivUpdateTicketDtoGen = {
      body: this.body,
      projectId: this.projectId,
      tagIds: this.tags.map(tag => tag.id) || [],
      title: this.title,
      values: CustomFieldValue.buildCustomFieldValuesDto(this.values || []),
      assignees: this.assignees,
      refs: Reference.filterDuplicates((this.refs ?? []) as IReference[]),
      state: this.state
    };

    if (this.due) {
      dto.due = this.due;
    }

    return dto;
  }

  get stateColor() {
    return ticketStateMap.get(this.state || TicketStatusEnum.DRAFT);
  }

  /**
   * Indicates if the given ticket has a due date
   */
  get isDue() {
    return this.due && this.state === TicketStatusEnum.OPEN;
  }

  /**
   * Indicates if the given ticket is overdue
   */
  get isOverdue() {
    if (this.due && this.state === TicketStatusEnum.OPEN) {
      return new Date(this.due) < new Date();
    }

    return false;
  }

  get titleReadable() {
    return `#${this.number} ${this.title}`;
  }

  constructor(ticket?: Partial<TicketBase | MrfiktivTicketViewModelGen>) {
    this.id = ticket?.id || "";
    this.partnerId = ticket?.partnerId || "";
    this.userId = ticket?.userId || "";
    this.user = ticket?.user || this.userId ? PartnerUserModule.maps.id.get(this.userId)[0] : undefined;
    this.timestamp = new Timestamp(ticket?.timestamp);
    this.number = ticket?.number as number;
    this.projectId = ticket?.projectId;
    this.title = ticket?.title || "";
    this.body = ticket?.body || "";
    this.tagIds = ticket?.tagIds ?? [];
    this.tags = (ticket?.tags ?? []).map(tag => new Tag(tag));
    this.assignees = ticket?.assignees || [];
    this.assigneesDetails = (ticket?.assigneesDetails || []).map(u => new ShortUser(u));
    this.state = (ticket?.state as TicketStatusEnum) || TicketStatusEnum.OPEN;
    this.closedAt = ticket?.closedAt;
    this.closedBy = ticket?.closedBy;
    this.closedByDetails = ticket?.closedByDetails ? new ShortUser(ticket.closedByDetails) : undefined;
    this.values = CustomFieldValue.buildCustomFieldValues(ticket?.values || []);
    this.refs = Reference.filterDuplicates((ticket?.refs ?? []).map(ref => new Reference(ref)));
    this.due = ticket?.due;
  }

  async fetch(): Promise<this> {
    try {
      this.loading = true;
      let fetched: MrfiktivTicketViewModelGen;

      if (this.id) {
        fetched = await ticketService.getOne(this.partnerId, this.id);
      } else if (this.number) {
        fetched = await ticketService.getOneByNumber(this.partnerId, this.number);
      } else {
        throw new Error("No id or number provided");
      }

      this.map(fetched);
      TicketDataAccessLayer.set(this);
      FleetAggregationModule.replaceTicket(this);
      this.loading = false;
    } catch (e) {
      this.loading = false;
      handleError(e);
    }

    return this;
  }

  map(ticket?: MrfiktivTicketViewModelGen) {
    if (!ticket) return;

    this.id = ticket.id;
    this.partnerId = ticket.partnerId;
    this.userId = ticket.userId;
    this.user = ticket.user || ticket.userId ? PartnerUserModule.maps.id.get(ticket.userId!)[0] : undefined;
    this.timestamp = new Timestamp(ticket?.timestamp);
    this.number = ticket.number;
    this.projectId = ticket.projectId;
    this.title = ticket.title ?? "";
    this.body = ticket.body ?? "";
    this.tagIds = ticket.tagIds ?? [];
    this.tags = (ticket.tags ?? []).map(tag => new Tag(tag));
    this.assignees.splice(0, this.assignees.length, ...(ticket.assignees ?? []));
    this.assigneesDetails = (ticket.assigneesDetails || []).map(u => new ShortUser(u));
    this.state = ticket.state as TicketStatusEnum;
    this.closedAt = ticket.closedAt;
    this.closedBy = ticket.closedBy;
    this.closedByDetails = ticket.closedByDetails ? new ShortUser(ticket.closedByDetails) : undefined;
    this.values = CustomFieldValue.buildCustomFieldValues(ticket?.values || []);
    this.refs = Reference.filterDuplicates((ticket?.refs ?? []).map(ref => new Reference(ref)));
    this.due = ticket.due;
  }

  async create() {
    try {
      const data: MrfiktivCreateTicketDtoGen = {
        title: this.title,
        body: this.body,
        projectId: this.projectId ? this.projectId : undefined,
        tagIds: this.tags.map(tag => tag.id) || [],
        values: CustomFieldValue.buildCustomFieldValuesDto(this.values || []),
        assignees: this.assignees,
        refs: Reference.filterDuplicates((this.refs ?? []) as IReference[]),
        state: this.state,
        due: this.due
      };
      const res = await ticketService.create(this.partnerId, data);

      this.map(res);

      if (this.assignees?.length) this.createAssigneeActivity(ActivityTypeEnum.CREATE_ASSIGNEE, this.assignees);

      TicketDataAccessLayer.set(this);
      FleetAggregationModule.parseTickets([this]);
    } catch (e) {
      handleError(e);
    }

    return this;
  }

  async delete(silent?: boolean) {
    try {
      const res = await ticketService.delete(this.partnerId, this.id);

      TicketDataAccessLayer.delete(this);
      FleetAggregationModule.removeTicket(res.number);
    } catch (e) {
      handleError(e);
      if (!silent) throw e;
    }
  }

  async update() {
    try {
      this.loading = true;
      const data: MrfiktivUpdateTicketDtoGen = {
        title: this.title,
        body: this.body,
        projectId: this.projectId,
        tagIds: this.tags.map(tag => tag.id) || [],
        values: CustomFieldValue.buildCustomFieldValuesDto(this.values || []),
        assignees: this.assignees,
        refs: Reference.filterDuplicates((this.refs ?? []) as IReference[]),
        state: this.state,
        due: this.due
      };
      const res = await ticketService.update(this.partnerId, this.id, data);
      this.map(res);
      TicketDataAccessLayer.set(this);
      FleetAggregationModule.replaceTicket(this);
      this.loading = false;
    } catch (e) {
      handleError(e);
      this.loading = false;
    }

    return this;
  }

  async updatePartial(dto: MrfiktivUpdateTicketDtoGen) {
    if (dto.values) {
      dto.values = CustomFieldValue.buildCustomFieldValuesDto((dto.values as ICustomFieldValue[]) || []);
    }

    try {
      this.loading = true;
      const res = await ticketService.update(this.partnerId, this.id, dto);

      this.map(res);

      TicketDataAccessLayer.set(this);
      FleetAggregationModule.replaceTicket(this);
      this.loading = false;
    } catch (e) {
      handleError(e);
      this.loading = false;
    }

    return this;
  }

  async updateState(state: TicketStatusEnum) {
    this.loading = true;

    try {
      const data: MrfiktivUpdateTicketDtoGen = {
        state
      };
      const res = await ticketService.update(this.partnerId, this.id, data);

      this.map(res);
      TicketDataAccessLayer.set(this);
      FleetAggregationModule.replaceTicket(this);

      if (res) {
        Vue.$log.debug(res);
        await new ActivityLog({
          partnerId: this.partnerId,
          source: {
            refType: BackendResourceEnum.TICKET,
            refId: res.id
          },
          actionType: ActionEnum.UPDATE,
          activity: ActivityTypeEnum.UPDATE_PROGRESS_STATUS,
          comment: state
        }).create();
      }
      this.loading = false;
    } catch (e) {
      Vue.$log.error(e);
      this.loading = false;
      throw e;
    }

    return this;
  }

  async createAssigneeActivity(
    activityType: ActivityTypeEnum.CREATE_ASSIGNEE | ActivityTypeEnum.DELETE_ASSIGNEE,
    newAssignees?: string[]
  ) {
    if (!newAssignees?.length) return;

    await new ActivityLog().createAssigneeActivity({
      partnerId: this.partnerId,
      source: {
        refType: BackendResourceEnum.TICKET,
        refId: this.id
      },
      newAssignees,
      activityType
    });
  }

  goTo(router: VueRouter) {
    return {
      table: () =>
        new TicketGoToHelper(router).goToTicketTable({
          partnerId: this.partnerId
        }),
      detail: () =>
        new TicketGoToHelper(router).goToTicketDetail({
          partnerId: this.partnerId,
          ticketId: this.id
        })
    };
  }
}
type ITicket = TicketBase;
const Ticket = Filter.createForClass(TicketBase);

export { ITicket, Ticket };
